import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import moment from "moment";
import { applyResolvedImagesToDom } from "./mealPlanImageResolve";

/**
 * The print host sits off-screen at opacity 0. html2canvas then keeps the
 * page size but paints almost nothing, which is the blank gap in the PDF.
 * Bring it on-screen only while a page is captured.
 */
function withPrintHostVisible(pageEl, run) {
  const host = pageEl.closest(".mpi-print-host");
  if (!host) return run();
  const previous = host.className;
  host.classList.add("is-capturing");
  return Promise.resolve(run()).finally(() => {
    host.className = previous;
  });
}

/**
 * Capture one print page at high resolution and add it to the PDF.
 * If content is somehow taller than A4, slice into extra pages instead of
 * shrinking (shrinking made text unreadable / looked like missing options).
 */
async function addDomPageToPdf(pdf, pageEl, { isFirst }) {
  const canvas = await withPrintHostVisible(pageEl, () =>
    html2canvas(pageEl, {
      scale: 2,
      backgroundColor: "#ffffff",
      logging: false,
      useCORS: true,
      allowTaint: true,
      imageTimeout: 20000,
      scrollX: 0,
      scrollY: -window.scrollY,
    })
  );

  // Always slice against a full A4 sheet. After a short page is shrunk,
  // pdf.internal.pageSize is that shorter height and would cut the next page.
  const pageWidth = 210;
  const pageHeight = 297;
  const pxPerMm = canvas.width / pageWidth;
  const pageHeightPx = Math.max(1, Math.floor(pageHeight * pxPerMm));

  let srcY = 0;
  let sliceIndex = 0;

  while (srcY < canvas.height) {
    const slicePx = Math.min(pageHeightPx, canvas.height - srcY);
    if (srcY > 0 && slicePx < pxPerMm * 12) break;
    const sliceCanvas = document.createElement("canvas");
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = slicePx;
    const ctx = sliceCanvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
    ctx.drawImage(
      canvas,
      0,
      srcY,
      canvas.width,
      slicePx,
      0,
      0,
      canvas.width,
      slicePx
    );

    const imgData = sliceCanvas.toDataURL("image/jpeg", 0.95);
    const imgH = slicePx / pxPerMm;

    if (!(isFirst && sliceIndex === 0)) {
      pdf.addPage("a4", "portrait");
    }

    // A4 is taller than a short section. Shrink the page to the content
    // so the footer is not left sitting under a blank region.
    pdf.internal.pageSize.setHeight(imgH);
    pdf.addImage(imgData, "JPEG", 0, 0, pageWidth, imgH, undefined, "FAST");

    srcY += slicePx;
    sliceIndex += 1;

    // Safety: one print page should rarely need more than 2 slices
    if (sliceIndex > 4) break;
  }
}

export async function renderIntakeFormPagesToPdf(printRootEl) {
  if (!printRootEl) throw new Error("Print element not found");

  const pages = printRootEl.querySelectorAll(".mpi-print-page");
  if (!pages.length) throw new Error("No diet plan pages found");

  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });

  for (let i = 0; i < pages.length; i++) {
    await addDomPageToPdf(pdf, pages[i], { isFirst: i === 0 });
  }

  return pdf;
}

export async function downloadMealPlanPackagePdf({
  intakePrintRoot,
  meta,
  fetchImageData,
}) {
  if (!intakePrintRoot) {
    throw new Error("Diet plan print content not found");
  }

  if (fetchImageData) {
    await applyResolvedImagesToDom(intakePrintRoot, fetchImageData);
  }

  // Give the browser a tick so images/fonts settle before capture
  await new Promise((r) => setTimeout(r, 120));

  const doc = await renderIntakeFormPagesToPdf(intakePrintRoot);
  const safe =
    (meta?.clientName || "diet_plan").replace(/[^\w\-]+/g, "_").slice(0, 40) ||
    "diet_plan";
  doc.save(
    `Diet_Plan_${safe}_${meta?.planId || moment().format("YYYY-MM-DD")}.pdf`
  );
}

export async function downloadIntakeFormPdf(printRootEl, fileName) {
  const pdf = await renderIntakeFormPagesToPdf(printRootEl);
  const safe = (fileName || "diet_plan").replace(/[^\w\-]+/g, "_");
  pdf.save(`Diet_Plan_${safe}.pdf`);
}
